import { cn } from '@/shared/lib/cn';

interface LogoIconProps {
  className?: string;
  size?: number;
  variant?: 'default' | 'light' | 'dark' | 'mono';
}

// Logo icon - Two Halves design symbolizing two people completing each other
export function LogoIcon({ className, size = 40, variant = 'default' }: LogoIconProps) {
  const colors = {
    default: {
      primary: 'var(--primary)',
      accent: 'var(--accent)',
      heart: '#FFFFFF',
    },
    light: {
      primary: 'rgba(255,255,255,0.95)',
      accent: 'rgba(255,255,255,0.7)',
      heart: 'var(--primary)',
    },
    dark: {
      primary: 'var(--primary-dark)',
      accent: 'var(--accent-dark)',
      heart: '#FFFFFF',
    },
    mono: {
      primary: 'currentColor',
      accent: 'currentColor',
      heart: 'var(--background)',
    },
  };

  const { primary, accent, heart } = colors[variant];

  return (
    <svg
      role="img"
      aria-label="Zawajme"
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('flex-shrink-0', className)}
    >
      {/* Left half - curved inward */}
      <path
        d="M6 10
           Q6 6.67 10 6.67
           L18.33 6.67
           Q18.33 11.67 15 15
           Q11.67 18.33 15 21.67
           Q18.33 25 18.33 30
           L18.33 33.33
           L10 33.33
           Q6 33.33 6 30
           Z"
        fill={primary}
      />

      {/* Right half - curved outward to fit */}
      <path
        d="M21.67 6.67
           L30 6.67
           Q33.33 6.67 33.33 10
           L33.33 30
           Q33.33 33.33 30 33.33
           L21.67 33.33
           L21.67 30
           Q21.67 25 25 21.67
           Q28.33 18.33 25 15
           Q21.67 11.67 21.67 6.67
           Z"
        fill={accent}
      />

      {/* Small heart at connection point */}
      <path
        d="M20 21.67
           L18.33 20
           C17.67 19.33 17.67 18.33 18.33 17.67
           C19 17 20 17 20 17.67
           C20 17 21 17 21.67 17.67
           C22.33 18.33 22.33 19.33 21.67 20
           L20 21.67Z"
        fill={heart}
      />
    </svg>
  );
}

// Full logo with text for landing page
export function LogoFull({
  className,
  size = 'md',
}: { className?: string; size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const sizes = {
    sm: { icon: 32, text: 'text-lg' },
    md: { icon: 40, text: 'text-xl' },
    lg: { icon: 56, text: 'text-2xl' },
    xl: { icon: 72, text: 'text-3xl' },
  };

  const { icon, text } = sizes[size];

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <LogoIcon size={icon} />
      <span className={cn('font-display font-semibold', text)}>Zawajme</span>
    </div>
  );
}

// Navbar logo - compact version
export function LogoNavbar({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <LogoIcon size={36} />
      <span className="font-display font-semibold text-lg hidden sm:block">Zawajme</span>
    </div>
  );
}

// Landing page hero logo - larger and more decorative
export function LogoHero({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div className="relative">
        {/* Glow effect */}
        <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-2xl scale-150" />

        {/* Main logo */}
        <div className="relative">
          <LogoIcon size={96} className="drop-shadow-xl" />
        </div>

        {/* Decorative dots */}
        <div className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-accent shadow-[var(--shadow-accent)]" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 rounded-full bg-accent/60" />
      </div>

      <h1 className="mt-6 font-display text-4xl font-semibold tracking-tight">Zawajme</h1>
      <p className="mt-2 text-lg text-muted-foreground">L'union qui vous ressemble</p>
    </div>
  );
}
